import { Camera, Upload } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import { getSites } from '../Inventory/inventoryOnboardingApi';
import MultiSelectDialog from '../Onboarding/MultiSelectDialog';
import { Button, Card } from '../ui';
import PrimaryButton from '../ui/PrimaryButton';
import Input from '../ui/input';
import { vendorApi } from './vendorApi';

const createEmptyPoc = () => ({
  name: '',
  role: '',
  mobileNo: '',
  email: '',
});

// Validation functions
const validatePAN = (pan) => {
  if (!pan || pan.trim() === '') return '';
  // PAN format: ABCDE1234F (5 letters, 4 digits, 1 letter)
  const panRegex = /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/;
  return panRegex.test(pan.toUpperCase()) ? '' : 'PAN number must be in format ABCDE1234F (5 letters, 4 digits, 1 letter)';
};

const validateAccountNumber = (accountNo) => {
  if (!accountNo || accountNo.trim() === '') return '';
  // Account number should be 9-18 digits
  const accountRegex = /^[0-9]{9,18}$/;
  return accountRegex.test(accountNo) ? '' : 'Account number must be 9-18 digits';
};

const validateMobile = (mobile) => {
  if (!mobile || mobile.trim() === '') return '';
  // Remove spaces and +91 prefix if present
  const cleaned = mobile.replace(/\s+/g, '').replace(/^\+91/, '');
  // Should be 10 digits
  const mobileRegex = /^[0-9]{10}$/;
  return mobileRegex.test(cleaned) ? '' : 'Mobile number must be 10 digits (e.g., 9876543210)';
};

const validateIFSC = (ifsc) => {
  if (!ifsc || ifsc.trim() === '') return '';
  // IFSC format: 4 letters + 0 (zero) + 6 alphanumeric (11 characters total)
  // Examples: SBIN0001234, HDFC0000123, ICIC0000001, ADBG0BSD123
  // Remove any spaces before validation
  const cleaned = ifsc.trim().toUpperCase();
  
  // Check length first
  if (cleaned.length !== 11) {
    return 'IFSC code must be exactly 11 characters (4 letters + 0 + 6 alphanumeric)';
  }
  
  // Check if 5th character is 0
  if (cleaned[4] !== '0') {
    return 'IFSC code must have 0 (zero) as the 5th character. Format: ABCD0EFGHIJ';
  }
  
  const ifscRegex = /^[A-Z]{4}0[A-Z0-9]{6}$/;
  if (!ifscRegex.test(cleaned)) {
    return 'IFSC code must be in format ABCD0EFGHIJ (4 letters, 0, 6 alphanumeric characters). Example: ADBG0BSD123';
  }
  return '';
};

const validateGST = (gst) => {
  if (!gst || gst.trim() === '') return '';
  // GST format: 15 characters
  // 2 digits (state code) + 5 letters (PAN part 1) + 4 digits (PAN part 2) + 1 letter (PAN part 3) + 1 char (entity: 1-9 or A-Z) + Z + 1 alphanumeric (checksum)
  // Examples: 22AAAAA0000A1Z5, 07ABCDE1234F1Z6, 29AAGCB7383J1Z4
  const cleaned = gst.trim().toUpperCase();
  const gstRegex = /^\d{2}[A-Z]{5}\d{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  if (!gstRegex.test(cleaned)) {
    return 'GST number must be in format 22AAAAA0000A1Z5 (2 digits, 5 letters, 4 digits, 1 letter, 1 char 1-9/A-Z, Z, 1 alphanumeric)';
  }
  return '';
};

const validatePincode = (pincode) => {
  if (!pincode || pincode.trim() === '') return '';
  // Pincode should be 6 digits
  const pincodeRegex = /^[0-9]{6}$/;
  return pincodeRegex.test(pincode) ? '' : 'Pincode must be 6 digits';
};

const VendorOnboarding = () => {
  // Log screen view
  useScreenLogging('VendorOnboarding', {
    component: 'VendorOnboarding',
    screenType: 'vendor-onboarding',
  });

  const [vendorName, setVendorName] = useState('');
  const [billingAddress, setBillingAddress] = useState({
    email: '',
    address: '',
    country: '',
    pinCode: '',
    city: '',
    state: '',
  });
  const [bankDetails, setBankDetails] = useState({
    panNumber: '',
    gstNumber: '',
    bankName: '',
    branchAddress: '',
    accountNo: '',
    ifscNo: '',
  });
  const [itemsProcure, setItemsProcure] = useState(['']);
  const [pocDetails, setPocDetails] = useState({
    poc1: createEmptyPoc(),
    poc2: createEmptyPoc(),
  });
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [isLoadingSites, setIsLoadingSites] = useState(false);
  const [siteError, setSiteError] = useState('');
  const [showSiteDialog, setShowSiteDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedback, setFeedback] = useState({ success: '', error: '' });
  const [logoData, setLogoData] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [hasLogo, setHasLogo] = useState(false);
  const fileInputRef = useRef(null);
  
  // Validation errors state
  const [validationErrors, setValidationErrors] = useState({
    panNumber: '',
    gstNumber: '',
    accountNo: '',
    ifscNo: '',
    pinCode: '',
    poc1MobileNo: '',
    poc2MobileNo: '',
  });

  useEffect(() => {
    const fetchSites = async () => {
      setIsLoadingSites(true);
      setSiteError('');
      try {
        const siteList = await getSites();
        // Sort sites alphabetically by site_name or name
        const sortedSites = [...(siteList || [])].sort((a, b) =>
          (a?.site_name || a?.name || '').localeCompare(b?.site_name || b?.name || '')
        );
        setSites(sortedSites);
      } catch (error) {
        setSiteError('Unable to load sites. Please try again later.');
      } finally {
        setIsLoadingSites(false);
      }
    };

    fetchSites();
  }, []);

  // Cleanup logo preview URL on unmount
  useEffect(() => {
    return () => {
      if (logoPreview && logoPreview.startsWith('blob:')) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleBillingAddressChange = (field, value) => {
    setBillingAddress((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFeedback({ success: '', error: '' });
    
    // Validate pincode on change
    if (field === 'pinCode') {
      const error = validatePincode(value);
      setValidationErrors((prev) => ({
        ...prev,
        pinCode: error,
      }));
    }
  };

  const handleBankDetailsChange = (field, value) => {
    setBankDetails((prev) => ({
      ...prev,
      [field]: value,
    }));
    setFeedback({ success: '', error: '' });
    
    // Validate on change
    let error = '';
    if (field === 'panNumber') {
      error = validatePAN(value);
      setValidationErrors((prev) => ({ ...prev, panNumber: error }));
    } else if (field === 'gstNumber') {
      error = validateGST(value);
      setValidationErrors((prev) => ({ ...prev, gstNumber: error }));
    } else if (field === 'accountNo') {
      error = validateAccountNumber(value);
      setValidationErrors((prev) => ({ ...prev, accountNo: error }));
    } else if (field === 'ifscNo') {
      error = validateIFSC(value);
      setValidationErrors((prev) => ({ ...prev, ifscNo: error }));
    }
  };

  const handleItemChange = (index, value) => {
    setItemsProcure((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
    setFeedback({ success: '', error: '' });
  };

  const handleAddItem = () => {
    setItemsProcure((prev) => [...prev, '']);
  };

  const handleRemoveItem = (index) => {
    if (itemsProcure.length === 1) {
      setItemsProcure(['']);
      return;
    }
    setItemsProcure((prev) => prev.filter((_, idx) => idx !== index));
  };

  const handlePocChange = (pocKey, field, value) => {
    setPocDetails((prev) => ({
      ...prev,
      [pocKey]: {
        ...prev[pocKey],
        [field]: value,
      },
    }));
    setFeedback({ success: '', error: '' });
    
    // Validate mobile number on change
    if (field === 'mobileNo') {
      const error = validateMobile(value);
      const errorKey = pocKey === 'poc1' ? 'poc1MobileNo' : 'poc2MobileNo';
      setValidationErrors((prev) => ({
        ...prev,
        [errorKey]: error,
      }));
    }
  };

  const isBillingAddressComplete = () =>
    !!billingAddress.email &&
    !!billingAddress.address &&
    !!billingAddress.country &&
    !!billingAddress.pinCode &&
    !!billingAddress.city &&
    !!billingAddress.state;

  const isBankDetailsComplete = () =>
    !!bankDetails.panNumber &&
    !!bankDetails.gstNumber &&
    !!bankDetails.bankName &&
    !!bankDetails.branchAddress &&
    !!bankDetails.accountNo &&
    !!bankDetails.ifscNo;

  const areItemsValid = itemsProcure.some((item) => item.trim().length > 0);
  const hasSelectedSites = selectedSites.length > 0;

  const isPocComplete = () => {
    // Only POC1 (primary_contact) is required
    // POC2 (secondary_contact) is optional
    const poc1 = pocDetails.poc1;
    return !!(poc1.name && poc1.role && poc1.mobileNo && poc1.email);
  };

  const isFormValid =
    !!vendorName.trim() &&
    hasSelectedSites &&
    isBillingAddressComplete() &&
    isBankDetailsComplete() &&
    areItemsValid &&
    isPocComplete();

  const handleSiteSelectionConfirm = (selectedIds) => {
    const selectedSiteData = sites.filter((site) =>
      selectedIds.includes(site.site_id || site.id)
    );
    setSelectedSites(selectedSiteData);
    setFeedback({ success: '', error: '' });
  };

  const handleRemoveSite = (siteId) => {
    setSelectedSites((prev) =>
      prev.filter((site) => (site.site_id || site.id) !== siteId)
    );
    setFeedback({ success: '', error: '' });
  };

  const handleLogoUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file size before processing (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      setFeedback({
        success: '',
        error: `Image file is too large (${(file.size / (1024 * 1024)).toFixed(2)} MB). Maximum allowed size is 5MB.`,
      });
      return;
    }

    // Check file type
    if (!file.type.startsWith('image/')) {
      setFeedback({ success: '', error: 'Please select a valid image file.' });
      return;
    }

    try {
      setFeedback({ success: '', error: '' });

      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target.result.split(',')[1];
        setLogoData(base64);
        setLogoPreview(e.target.result);
        setHasLogo(true);
        setFeedback({ success: '', error: '' });
      };

      reader.onerror = () => {
        setFeedback({ success: '', error: 'Failed to read image file. Please try again.' });
      };

      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing image:', error);
      setFeedback({ success: '', error: 'Failed to process image. Please try again.' });
    }
  };

  const handleRemoveLogo = () => {
    if (logoPreview && logoPreview.startsWith('blob:')) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoData(null);
    setLogoPreview(null);
    setHasLogo(false);
    setFeedback({ success: '', error: '' });
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validate all fields before submission
    // Only validate format if value is present (not checking required, just format)
    const errors = {
      panNumber: validatePAN(bankDetails.panNumber),
      gstNumber: validateGST(bankDetails.gstNumber),
      accountNo: validateAccountNumber(bankDetails.accountNo),
      ifscNo: validateIFSC(bankDetails.ifscNo),
      pinCode: validatePincode(billingAddress.pinCode),
      poc1MobileNo: validateMobile(pocDetails.poc1.mobileNo),
      poc2MobileNo: validateMobile(pocDetails.poc2.mobileNo),
    };
    
    setValidationErrors(errors);
    
    // Check if there are any validation errors
    const hasValidationErrors = Object.values(errors).some(error => error !== '');
    if (hasValidationErrors) {
      setFeedback({ success: '', error: 'Please fix the validation errors before submitting.' });
      return;
    }
    
    if (!isFormValid) {
      setFeedback({ success: '', error: 'Please fill all required fields.' });
      return;
    }

    setIsSubmitting(true);
    setFeedback({ success: '', error: '' });
    
    try {
      // Map form data to API format
      const poc1 = pocDetails.poc1;
      const poc2 = pocDetails.poc2;

      // Helper function to remove undefined, null, and empty string values from objects
      const cleanObject = (obj) => {
        if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
          return obj;
        }
        const cleaned = {};
        Object.keys(obj).forEach(key => {
          const value = obj[key];
          if (value !== undefined && value !== null && value !== '') {
            if (typeof value === 'object' && !Array.isArray(value)) {
              const cleanedNested = cleanObject(value);
              if (cleanedNested && Object.keys(cleanedNested).length > 0) {
                cleaned[key] = cleanedNested;
              }
            } else if (Array.isArray(value)) {
              // Keep arrays as-is if they have items
              if (value.length > 0) {
                cleaned[key] = value;
              }
            } else {
              cleaned[key] = value;
            }
          }
        });
        return cleaned;
      };

      // Build payload step by step, only including fields with values
      const apiPayload = {
        vendor_name: vendorName.trim(),
      };

      // Add optional top-level fields
      if (bankDetails.gstNumber?.trim()) {
        apiPayload.gst_number = bankDetails.gstNumber.trim();
      }
      if (bankDetails.panNumber?.trim()) {
        apiPayload.pan_number = bankDetails.panNumber.trim();
      }
      if (poc1.name?.trim()) {
        apiPayload.contact_person = poc1.name.trim();
      }
      if (poc1.email?.trim()) {
        apiPayload.contact_email = poc1.email.trim();
      }
      if (poc1.mobileNo?.trim()) {
        apiPayload.contact_phone = poc1.mobileNo.trim();
      }

      // Build billing address only if we have at least one field
      const billingAddressFields = {};
      if (billingAddress.email?.trim()) billingAddressFields.email = billingAddress.email.trim();
      if (billingAddress.address?.trim()) billingAddressFields.street = billingAddress.address.trim();
      if (billingAddress.city?.trim()) billingAddressFields.city = billingAddress.city.trim();
      if (billingAddress.state?.trim()) billingAddressFields.state = billingAddress.state.trim();
      if (billingAddress.pinCode?.trim()) billingAddressFields.pincode = billingAddress.pinCode.trim();
      if (billingAddress.country?.trim()) billingAddressFields.country = billingAddress.country.trim();
      
      // Add POC1 details to billing address if name exists
      if (poc1.name?.trim()) {
        const nameParts = poc1.name.trim().split(' ');
        if (nameParts[0]) billingAddressFields.first_name = nameParts[0];
        if (nameParts.slice(1).join(' ')) billingAddressFields.last_name = nameParts.slice(1).join(' ');
      }
      if (poc1.mobileNo?.trim()) billingAddressFields.mobile_number = poc1.mobileNo.trim();
      if (bankDetails.panNumber?.trim()) billingAddressFields.pan_number = bankDetails.panNumber.trim();

      if (Object.keys(billingAddressFields).length > 0) {
        apiPayload.billing_address = billingAddressFields;
      }

      // Build bank details only if we have at least one field
      const bankDetailsFields = {};
      if (bankDetails.bankName?.trim()) bankDetailsFields.bank_name = bankDetails.bankName.trim();
      if (bankDetails.branchAddress?.trim()) {
        bankDetailsFields.branch_address = bankDetails.branchAddress.trim();
        bankDetailsFields.branch = bankDetails.branchAddress.trim();
      }
      if (bankDetails.accountNo?.trim()) bankDetailsFields.account_number = bankDetails.accountNo.trim();
      if (bankDetails.ifscNo?.trim()) bankDetailsFields.ifsc_code = bankDetails.ifscNo.trim();

      if (Object.keys(bankDetailsFields).length > 0) {
        apiPayload.bank_details = bankDetailsFields;
      }

      // Add items_supplied if we have any
      const filteredItems = itemsProcure.filter((item) => item.trim().length > 0);
      if (filteredItems.length > 0) {
        apiPayload.items_supplied = filteredItems.map(item => item.trim());
      }

      // Build secondary contact only if we have at least one field
      const secondaryContactFields = {};
      if (poc2.name?.trim()) secondaryContactFields.name = poc2.name.trim();
      if (poc2.role?.trim()) secondaryContactFields.role = poc2.role.trim();
      if (poc2.mobileNo?.trim()) secondaryContactFields.mobile = poc2.mobileNo.trim();
      if (poc2.email?.trim()) secondaryContactFields.email = poc2.email.trim();

      if (Object.keys(secondaryContactFields).length > 0) {
        apiPayload.secondary_contact = secondaryContactFields;
      }

      // Add site information
      if (selectedSites.length > 0) {
        apiPayload.site_ids = selectedSites.map((site) => site.site_id || site.id).filter(Boolean);
        if (selectedSites[0]?.site_name || selectedSites[0]?.name) {
          apiPayload.site_name = selectedSites[0].site_name || selectedSites[0].name;
        }
      }

      // Status is always active for new vendors
      apiPayload.status = 'active';

      console.log('Submitting vendor payload:', JSON.stringify(apiPayload, null, 2));
      console.log('Payload size:', JSON.stringify(apiPayload).length, 'bytes');

      // Validate that vendor_name is present (required field)
      if (!apiPayload.vendor_name || apiPayload.vendor_name.trim().length === 0) {
        setFeedback({ 
          success: '', 
          error: 'Vendor name is required.' 
        });
        setIsSubmitting(false);
        return;
      }

      // Validate that site_ids is present (required for multi-site support)
      if (!apiPayload.site_ids || apiPayload.site_ids.length === 0) {
        setFeedback({ 
          success: '', 
          error: 'At least one site must be selected.' 
        });
        setIsSubmitting(false);
        return;
      }

      // Log the final payload for debugging
      console.log('Final API payload:', apiPayload);
      console.log('Payload keys:', Object.keys(apiPayload));

      // Create vendor via API
      const result = await vendorApi.createVendor(apiPayload);

      if (result.success) {
        const createdVendor = result.vendor;
        
        // If logo was uploaded, handle logo upload via presign
        if (logoData && createdVendor?.vendor_id) {
          try {
            // Get file type from base64 data
            const contentType = logoPreview?.match(/data:([^;]+);/)?.[1] || 'image/jpeg';
            
            // Get presigned URL
            const presignResult = await vendorApi.presignLogoUpload(
              createdVendor.vendor_id,
              contentType
            );

            if (presignResult.success) {
              // Convert base64 to blob
              const base64Response = await fetch(logoPreview);
              const blob = await base64Response.blob();

              // Upload to S3 using presigned POST
              const formData = new FormData();
              Object.keys(presignResult.presigned_post.fields).forEach(key => {
                formData.append(key, presignResult.presigned_post.fields[key]);
              });
              formData.append('file', blob);

              const uploadResponse = await fetch(presignResult.presigned_post.url, {
                method: 'POST',
                body: formData,
              });

              if (uploadResponse.ok) {
                // Update vendor with logo key
                await vendorApi.updateVendor(createdVendor.vendor_id, {
                  logo_key: presignResult.object_key,
                  logo_content_type: presignResult.content_type,
                });
              }
            }
          } catch (logoError) {
            console.error('Error uploading logo:', logoError);
            // Don't fail the entire submission if logo upload fails
            setFeedback({ 
              success: 'Vendor onboarded successfully, but logo upload failed.', 
              error: '' 
            });
            return;
          }
        }

        setFeedback({ 
          success: 'Vendor onboarded successfully!', 
          error: '' 
        });

        // Reset form after successful submission
        setTimeout(() => {
          setVendorName('');
          setBillingAddress({
            email: '',
            address: '',
            country: '',
            pinCode: '',
            city: '',
            state: '',
          });
          setBankDetails({
            panNumber: '',
            gstNumber: '',
            bankName: '',
            branchAddress: '',
            accountNo: '',
            ifscNo: '',
          });
          setItemsProcure(['']);
          setPocDetails({
            poc1: createEmptyPoc(),
            poc2: createEmptyPoc(),
          });
          setSelectedSites([]);
          setLogoData(null);
          setLogoPreview(null);
          setHasLogo(false);
          if (fileInputRef.current) {
            fileInputRef.current.value = '';
          }
          // Clear success message when form is reset
          setFeedback({ success: '', error: '' });
        }, 2000);
      } else {
        // Provide more detailed error message for 502 errors
        let errorMessage = result.message || result.error || 'Failed to onboard vendor. Please try again.';
        
        if (result.error === 'Bad Gateway' || result.status === 502) {
          errorMessage = 'Backend Error (502): The vendor API is currently unavailable. This is a server-side issue, not a problem with your data. Please contact your system administrator or check the backend Lambda function configuration.';
        }
        
        setFeedback({ 
          success: '', 
          error: errorMessage
        });
        
        // Log technical details for debugging
        if (result.technicalDetails) {
          console.error('Vendor API Technical Error Details:', result.technicalDetails);
        }
      }
    } catch (error) {
      console.error('Error submitting vendor:', error);
      setFeedback({ 
        success: '', 
        error: error.message || 'Failed to onboard vendor. Please try again.' 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderPocSection = (pocKey, title) => {
    const data = pocDetails[pocKey];
    const isPoc1 = pocKey === 'poc1';
    const displayTitle = isPoc1 ? title : `${title} (optional)`;
    
    return (
      <div className="space-y-4 rounded-2xl border border-white/30 bg-white/40 p-4 shadow-sm backdrop-blur">
        <h3 className="text-base font-semibold text-gray-900">{displayTitle}</h3>
        <div className="grid grid-cols-1 gap-4">
          <div>
            <label className="mb-1 block text-sm text-gray-600">
              Name {isPoc1 && <span className="text-red-500">*</span>}
            </label>
            <Input
              type="text"
              value={data.name}
              onChange={(e) => handlePocChange(pocKey, 'name', e.target.value)}
              placeholder="Enter name"
              className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-600">
              Role {isPoc1 && <span className="text-red-500">*</span>}
            </label>
            <Input
              type="text"
              value={data.role}
              onChange={(e) => handlePocChange(pocKey, 'role', e.target.value)}
              placeholder="Enter role"
              className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
            />
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-600">
              Mobile No. {isPoc1 && <span className="text-red-500">*</span>}
            </label>
            <Input
              type="tel"
              value={data.mobileNo}
              onChange={(e) => handlePocChange(pocKey, 'mobileNo', e.target.value)}
              onBlur={() => {
                const error = validateMobile(data.mobileNo);
                const errorKey = pocKey === 'poc1' ? 'poc1MobileNo' : 'poc2MobileNo';
                setValidationErrors((prev) => ({ ...prev, [errorKey]: error }));
              }}
              placeholder="+91 98765 43210"
              className={`h-11 rounded-2xl border px-4 text-sm text-gray-900 ${
                validationErrors[pocKey === 'poc1' ? 'poc1MobileNo' : 'poc2MobileNo'] 
                  ? 'border-red-500 bg-red-50' 
                  : 'bg-gray-50'
              }`}
            />
            {validationErrors[pocKey === 'poc1' ? 'poc1MobileNo' : 'poc2MobileNo'] && (
              <p className="mt-1 text-xs text-red-600">
                {validationErrors[pocKey === 'poc1' ? 'poc1MobileNo' : 'poc2MobileNo']}
              </p>
            )}
          </div>
          <div>
            <label className="mb-1 block text-sm text-gray-600">
              Email {isPoc1 && <span className="text-red-500">*</span>}
            </label>
            <Input
              type="email"
              value={data.email}
              onChange={(e) => handlePocChange(pocKey, 'email', e.target.value)}
              placeholder="name@company.com"
              className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
            />
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="w-full min-h-screen">
      <div className="mx-auto">
        <header className="mb-8 text-center">
          <h1 className="text-3xl font-semibold text-gray-900">Vendor Onboarding</h1>
          <p className="mt-2 text-gray-500">
            Add a new vendor to the system with complete details.
          </p>
        </header>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Row: Logo Upload (320px) + Vendor Information (flex) */}
          <div className="flex flex-col lg:flex-row gap-6">
            {/* Column 1: Logo Upload Section - Fixed 320px */}
            <div className="w-full lg:w-[320px] flex-shrink-0">
              <Card.Gradient className="p-6">
                <div className="text-center">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">
                    Company Logo (optional)
                  </h2>

                  <div className="flex flex-col items-center gap-4">
                    {/* Logo Preview */}
                    <div className="relative">
                      {logoPreview ? (
                        <div className="w-32 h-32 rounded-xl border-4 border-gray-300 overflow-hidden bg-white">
                          <img
                            src={logoPreview}
                            alt="Company Logo Preview"
                            className="w-full h-full object-contain"
                          />
                          <button
                            type="button"
                            onClick={handleRemoveLogo}
                            className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 transition-colors"
                            style={{ fontSize: '20px', lineHeight: '1' }}
                          >
                            ×
                          </button>
                        </div>
                      ) : (
                        <div className="w-32 h-32 rounded-xl border-4 border-dashed border-gray-300 flex items-center justify-center bg-gray-50">
                          <Camera className="w-12 h-12 text-gray-400" />
                        </div>
                      )}
                    </div>

                    {/* Upload Button */}
                    <div className="flex gap-4">
                      <Button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700"
                        style={{ color: '#ffffff' }}
                      >
                        <Upload className="w-4 h-4" style={{ color: '#ffffff' }} />
                        Upload Logo
                      </Button>
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </div>
                </div>
              </Card.Gradient>
            </div>

            {/* Column 2: Vendor Information Section - Flex */}
            <div className="flex-1">
              <Card.Gradient className="p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Vendor Information</h2>
                <div>
                  <label className="mb-1 block text-sm text-gray-600">Vendor Name(Company Name) <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={vendorName}
                    onChange={(e) => {
                      setVendorName(e.target.value);
                      setFeedback({ success: '', error: '' });
                    }}
                    placeholder="Enter vendor name"
                    className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                  />
                </div>
            <div className="mt-6 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-700"> Select Sites <span className="text-red-500">*</span></p>
                <Button
                  type="button"
                  onClick={() => setShowSiteDialog(true)}
                  disabled={isLoadingSites || sites.length === 0}
                  className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-xl"
                >
                  {isLoadingSites ? 'Loading...' : 'Select Sites'}
                </Button>
              </div>
              {siteError && <p className="text-sm text-red-600">{siteError}</p>}
              {!isLoadingSites && sites.length === 0 && (
                <p className="text-sm text-gray-500">No sites available to select.</p>
              )}
              {selectedSites.length > 0 ? (
                <div className="space-y-2">
                  {selectedSites.map((site) => (
                    <div
                      key={site.site_id || site.id}
                      className="flex items-start justify-between rounded-xl border border-gray-200 bg-white/60 p-3"
                    >
                      <div>
                        <p className="font-semibold text-gray-900">{site.site_name || 'Unknown Site'}</p>
                        <p className="text-xs text-gray-500">
                          {site.address || 'Location not specified'}
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveSite(site.site_id || site.id)}
                        className="text-sm text-red-600 hover:text-red-800"
                      >
                        Remove
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500">No sites selected yet.</p>
              )}
            </div>
              </Card.Gradient>
            </div>
          </div>

          {/* Billing Address Section */}
          <Card.Gradient className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Billing Address</h2>
            <div className="space-y-4">
              <div>
                <label className="mb-1 block text-sm text-gray-600">Email Address <span className="text-red-500">*</span></label>
                <Input
                  type="email"
                  value={billingAddress.email}
                  onChange={(e) => handleBillingAddressChange('email', e.target.value)}
                  placeholder="name@company.com"
                  className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-600">Address <span className="text-red-500">*</span></label>
                <Input
                  type="text"
                  value={billingAddress.address}
                  onChange={(e) => handleBillingAddressChange('address', e.target.value)}
                  placeholder="Street, Building, Area"
                  className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm text-gray-600">Country <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={billingAddress.country}
                    onChange={(e) => handleBillingAddressChange('country', e.target.value)}
                    placeholder="India"
                    className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-600">Pin Code <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={billingAddress.pinCode}
                    onChange={(e) => handleBillingAddressChange('pinCode', e.target.value)}
                    onBlur={() => {
                      const error = validatePincode(billingAddress.pinCode);
                      setValidationErrors((prev) => ({ ...prev, pinCode: error }));
                    }}
                    placeholder="500001"
                    maxLength={6}
                    className={`h-11 rounded-2xl border px-4 text-sm text-gray-900 ${
                      validationErrors.pinCode ? 'border-red-500 bg-red-50' : 'bg-gray-50'
                    }`}
                  />
                  {validationErrors.pinCode && (
                    <p className="mt-1 text-xs text-red-600">{validationErrors.pinCode}</p>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm text-gray-600">City <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={billingAddress.city}
                    onChange={(e) => handleBillingAddressChange('city', e.target.value)}
                    placeholder="Hyderabad"
                    className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-600">State <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={billingAddress.state}
                    onChange={(e) => handleBillingAddressChange('state', e.target.value)}
                    placeholder="Telangana"
                    className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                  />
                </div>
              </div>
            </div>
          </Card.Gradient>

          {/* Bank Details Card */}
          <Card.Gradient className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Financial Details</h2>
            <div className="space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm text-gray-600">PAN Number <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={bankDetails.panNumber}
                    onChange={(e) => handleBankDetailsChange('panNumber', e.target.value.toUpperCase())}
                    onBlur={() => {
                      const error = validatePAN(bankDetails.panNumber);
                      setValidationErrors((prev) => ({ ...prev, panNumber: error }));
                    }}
                    placeholder="ABCDE1234F"
                    maxLength={10}
                    className={`h-11 rounded-2xl border px-4 text-sm text-gray-900 ${
                      validationErrors.panNumber ? 'border-red-500 bg-red-50' : 'bg-gray-50'
                    }`}
                  />
                  {validationErrors.panNumber && (
                    <p className="mt-1 text-xs text-red-600">{validationErrors.panNumber}</p>
                  )}
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-600">GST Number <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={bankDetails.gstNumber}
                    onChange={(e) => handleBankDetailsChange('gstNumber', e.target.value.toUpperCase())}
                    onBlur={() => {
                      const error = validateGST(bankDetails.gstNumber);
                      setValidationErrors((prev) => ({ ...prev, gstNumber: error }));
                    }}
                    placeholder="22AAAAA0000A1Z5"
                    maxLength={15}
                    className={`h-11 rounded-2xl border px-4 text-sm text-gray-900 ${
                      validationErrors.gstNumber ? 'border-red-500 bg-red-50' : 'bg-gray-50'
                    }`}
                  />
                  {validationErrors.gstNumber && (
                    <p className="mt-1 text-xs text-red-600">{validationErrors.gstNumber}</p>
                  )}
                </div>
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-600">Bank Name <span className="text-red-500">*</span></label>
                <Input
                  type="text"
                  value={bankDetails.bankName}
                  onChange={(e) => handleBankDetailsChange('bankName', e.target.value)}
                  placeholder="Enter bank name"
                  className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm text-gray-600">Branch Address <span className="text-red-500">*</span></label>
                <Input
                  type="text"
                  value={bankDetails.branchAddress}
                  onChange={(e) => handleBankDetailsChange('branchAddress', e.target.value)}
                  placeholder="Enter branch address"
                  className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm text-gray-600">Account No. <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={bankDetails.accountNo}
                    onChange={(e) => {
                      const numericValue = e.target.value.replace(/\D/g, '');
                      handleBankDetailsChange('accountNo', numericValue);
                    }}
                    onBlur={() => {
                      const error = validateAccountNumber(bankDetails.accountNo);
                      setValidationErrors((prev) => ({ ...prev, accountNo: error }));
                    }}
                    placeholder="Enter account number"
                    minLength={7}
                    maxLength={32}
                    className={`h-11 rounded-2xl border px-4 text-sm text-gray-900 ${
                      validationErrors.accountNo ? 'border-red-500 bg-red-50' : 'bg-gray-50'
                    }`}
                  />
                  {validationErrors.accountNo && (
                    <p className="mt-1 text-xs text-red-600">{validationErrors.accountNo}</p>
                  )}
                </div>
                <div>
                  <label className="mb-1 block text-sm text-gray-600">IFSC No. <span className="text-red-500">*</span></label>
                  <Input
                    type="text"
                    value={bankDetails.ifscNo}
                    onChange={(e) => handleBankDetailsChange('ifscNo', e.target.value.toUpperCase())}
                    onBlur={() => {
                      const error = validateIFSC(bankDetails.ifscNo);
                      setValidationErrors((prev) => ({ ...prev, ifscNo: error }));
                    }}
                    placeholder="Enter IFSC code"
                    maxLength={11}
                    className={`h-11 rounded-2xl border px-4 text-sm text-gray-900 ${
                      validationErrors.ifscNo ? 'border-red-500 bg-red-50' : 'bg-gray-50'
                    }`}
                  />
                  {validationErrors.ifscNo && (
                    <p className="mt-1 text-xs text-red-600">{validationErrors.ifscNo}</p>
                  )}
                </div>
              </div>
            </div>
          </Card.Gradient>

          {/* POC Details Card */}
          <Card.Gradient className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Point of Contact Details</h2>
            <div className="grid gap-6 md:grid-cols-2">
              {renderPocSection('poc1', 'POC 1 Details')}
              {renderPocSection('poc2', 'POC 2 Details')}
            </div>
          </Card.Gradient>

          {/* Items Procure Card */}
          <Card.Gradient className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900">Items Procure <span className="text-red-500">*</span></h2>
              <button
                type="button"
                onClick={handleAddItem}
                className="inline-flex items-center gap-2 rounded-full border border-dashed border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:border-gray-400"
              >
                Add Item
              </button>
            </div>
            <div className="space-y-3">
              {itemsProcure.map((item, index) => (
                <div key={`item-${index}`} className="flex items-center gap-3">
                  <div className="flex-1">
                    <Input
                      type="text"
                      value={item}
                      onChange={(e) => handleItemChange(index, e.target.value)}
                      placeholder={`Item ${index + 1}`}
                      className="h-11 rounded-2xl border bg-gray-50 px-4 text-sm text-gray-900"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => handleRemoveItem(index)}
                    className={`inline-flex items-center gap-1 rounded-full border px-3 py-2 text-xs font-semibold transition-colors ${
                      index === 0 && itemsProcure.length === 1
                        ? 'border-gray-200 text-gray-600 hover:border-gray-300'
                        : 'border-red-100 text-red-600 hover:border-red-300'
                    }`}
                  >
                    {index === 0 && itemsProcure.length === 1 ? 'Clear' : 'Remove'}
                  </button>
                </div>
              ))}
            </div>
          </Card.Gradient>

          

          {/* Feedback Messages */}
          {feedback.error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {feedback.error}
            </div>
          )}
          {feedback.success && (
            <div className="rounded-2xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {feedback.success}
            </div>
          )}

          {/* Submit Button */}
          <div className="flex justify-end">
            <PrimaryButton type="submit" disabled={!isFormValid || isSubmitting}>
              {isSubmitting ? 'Onboarding...' : 'Onboard Vendor'}
            </PrimaryButton>
          </div>
        </form>
      </div>

      {showSiteDialog &&
        createPortal(
          <MultiSelectDialog
            isOpen={showSiteDialog}
            onClose={() => setShowSiteDialog(false)}
            onConfirm={(selectedIds) => {
              handleSiteSelectionConfirm(selectedIds);
              setShowSiteDialog(false);
            }}
            entityType="site"
            dialogTitle="Select Site"
            availableEntities={sites}
            selectedEntityIds={selectedSites.map((site) => site.site_id || site.id)}
          />,
          document.body
        )}
    </div>
  );
};

export default VendorOnboarding;
